import { createRouter, createWebHistory } from 'vue-router'
import { requireNoAuth } from '@/services/auth'
import Home from '@/views/Home.vue'
import store from '@/store/'
import { useToast } from 'vue-toastification'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    // Marketing landing page.
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/dashboard',
    name: 'CampaignCharts',
    component: () => import('../views/CampaignCharts.vue'),
    meta: {
      groupsAllowed: [
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'Fundraiser',
        'NPO Admin',
        'Givaroo Staff',
      ],
    },
    props: {
      pageTitle: 'Dashboard',
    },
  },
  {
    path: '/fundraiserPerformanceCharts',
    name: 'FundraiserPerformanceCharts',
    component: () => import('../views/FundraiserPerformanceCharts.vue'),
    meta: {
      groupsAllowed: [
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'NPO Admin',
        'Givaroo Staff',
      ],
    },
    props: {
      pageTitle: 'Dashboard',
    },
  },
  {
    path: '/login',
    name: 'Login',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue'),
    beforeEnter: requireNoAuth,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/signup',
    name: 'Signup',
    component: () => import(/* webpackChunkName: "signup" */ '../views/Signup.vue'),
    beforeEnter: requireNoAuth,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/password/request-reset',
    name: 'RequestPasswordReset',
    component: () =>
      import(/* webpackChunkName: "requestReset" */ '../views/RequestPasswordReset.vue'),
    beforeEnter: requireNoAuth,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/password/reset/confirm/:uid/:token',
    name: 'ResetPassword',
    component: () => import(/* webpackChunkName: "confirmReset" */ '../views/ResetPassword.vue'),
    beforeEnter: requireNoAuth,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/donation/:uniqueId/verification',
    name: 'DonationVerification',
    component: () => import('../views/DonationVerification.vue'),
    beforeEnter: requireNoAuth,
    // The donor opens this from a text on their own phone. The page carries the signed
    // grant from that link, which is what the backend checks.
    meta: {
      isPublic: true,
    },
  },

  {
    path: '/npos',
    name: 'NPOList',
    component: () => import(/* webpackChunkName: "npoList" */ '../views/NPOList.vue'),
    meta: {
      groupsAllowed: ['Givaroo Staff'],
    },
  },
  {
    path: '/npo/:id',
    name: 'NPODetail',
    component: () => import(/* webpackChunkName: "npoDetail" */ '../views/NPODetail.vue'),
    props: true,
    meta: {
      groupsAllowed: ['NPO Admin', 'Givaroo Staff'],
    },
  },
  {
    path: '/campaigns',
    name: 'CampaignList',
    component: () => import(/* webpackChunkName: "campaigns" */ '../views/CampaignList.vue'),
    props: {
      pageTitle: 'Campaigns',
    },
    meta: {
      groupsAllowed: [
        'NPO Admin',
        'Givaroo Staff',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'Fundraiser',
      ],
    },
  },
  {
    path: '/campaign/:id',
    name: 'CampaignData',
    component: () => import(/* webpackChunkName: "campaignData" */ '../views/CampaignData.vue'),
    // props: {
    //   pageTitle: 'Campaign Data',
    // },
    meta: {
      groupsAllowed: [
        'NPO Admin',
        'Givaroo Staff',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
      ],
    },
  },
  {
    path: '/fundraisers',
    name: 'FundraiserList',
    component: () => import(/* webpackChunkName: "fundraisers" */ '../views/FundraiserList.vue'),
    props: {
      pageTitle: 'Fundraisers',
    },
    meta: {
      groupsAllowed: [
        'NPO Admin',
        'Givaroo Staff',
        'Canvassing Org Field Director',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
      ],
    },
  },
  {
    path: '/donations',
    name: 'DonationList',
    component: () => import(/* webpackChunkName: "donationList" */ '../views/DonationList.vue'),
    props: {
      pageTitle: 'Donations',
    },
    meta: {
      groupsAllowed: [
        'Givaroo Staff',
        'NPO Admin',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'Fundraiser',
      ],
    },
  },
  {
    path: '/leads',
    name: 'LeadList',
    component: () => import(/* webpackChunkName: "leadList" */ '../views/LeadList.vue'),
    props: {
      pageTitle: 'Leads',
    },
    meta: {
      groupsAllowed: [
        'Givaroo Staff',
        'NPO Admin',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'Fundraiser',
      ],
    },
  },
  {
    path: '/campaign/:id/donate',
    name: 'CampaignDonate',
    component: () => import(/* webpackChunkName: "campaignDonate" */ '../views/CampaignDonate.vue'),
    // The donor form is reached by scanning a fundraiser's QR code on a phone with no
    // login. The signed grant in that link is what the backend checks.
    meta: {
      hideNavbar: false,
      isPublic: true,
    },
  },
  {
    path: '/npo-onboarding',
    name: 'NPOOnboarding',
    component: () => import(/* webpackChunkName: "npoOnboarding" */ '../views/NPOOnboarding.vue'),
    // Self-serve sign-up, linked from the signup page.
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/campaign/:id/lead',
    name: 'CampaignLead',
    component: () => import(/* webpackChunkName: "campaignLead" */ '../views/CampaignLead.vue'),
    meta: {
      hideNavbar: true,
      isPublic: true,
    },
  },
  {
    path: '/thank-you',
    name: 'ThankYou',
    component: () => import(/* webpackChunkName: "campaignData" */ '../views/ThankYou.vue'),
    props: {
      pageTitle: 'Thank You',
    },
    // Where a donor lands after submitting the form.
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/notifications',
    name: 'NotificationList',
    component: () =>
      import(/* webpackChunkName: "notifications" */ '../views/NotificationsList.vue'),
    meta: {
      groupsAllowed: [
        'Fundraiser',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'Givaroo Staff',
      ],
    },
  },
  {
    path: '/leaderboard',
    name: 'Leaderboard',
    component: () => import(/* webpackChunkName: "leaderboard" */ '../views/Leaderboard.vue'),
    props: {
      pageTitle: 'Leaderboard',
    },
    meta: {
      groupsAllowed: [
        'Fundraiser',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'NPO Admin',
        'Givaroo Staff',
      ],
    },
  },
  {
    path: '/achievements',
    name: 'Achievements',
    component: () => import(/* webpackChunkName: "achievements" */ '../views/Achievements.vue'),
    props: {
      pageTitle: 'Achievements',
    },
    meta: {
      groupsAllowed: ['Fundraiser'],
    },
  },
  {
    path: '/tasks',
    name: 'TaskList',
    component: () => import(/* webpackChunkName: "tasks" */ '../views/TaskList.vue'),
    props: {
      pageTitle: 'Tasks',
    },
    meta: {
      groupsAllowed: [
        'Fundraiser',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'Givaroo Staff',
      ],
    },
  },
  {
    path: '/library',
    name: 'LibraryList',
    component: () => import(/* webpackChunkName: "library" */ '../views/LibraryList.vue'),
    props: {
      pageTitle: 'Library',
    },
    meta: {
      groupsAllowed: [
        'Fundraiser',
        'Canvassing Org Admin',
        'Super Canvassing Org Admin',
        'Canvassing Org Field Director',
        'NPO Admin',
        'Givaroo Staff',
      ],
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'PageNotFound',
    component: () => import('../views/PageNotFound.vue'),
    // A mistyped URL should say so rather than bounce a visitor to the login page.
    meta: {
      isPublic: true,
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  linkActiveClass: 'active-link',
  linkExactActiveClass: 'exact-active-link',
})

// After HMR or a dev-server restart, lazy route chunks can 404 until the page reloads.
router.onError((error, to) => {
  const isChunkError =
    error?.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(error?.message || '')

  if (!isChunkError) {
    return
  }

  const reloadKey = `chunk-reload:${to.fullPath}`
  if (!sessionStorage.getItem(reloadKey)) {
    sessionStorage.setItem(reloadKey, '1')
    window.location.assign(to.fullPath)
  }
})

router.afterEach((to) => {
  sessionStorage.removeItem(`chunk-reload:${to.fullPath}`)
})

// Checks user groups returned in user object against groups defined in meta object above
const checkPermission = (groupsAllowed, usersGroups) => {
  if (usersGroups && groupsAllowed) {
    for (let i = 0; i < usersGroups.length; i++) {
      for (let j = 0; j < groupsAllowed.length; j++) {
        if (usersGroups[i].includes(groupsAllowed[j])) {
          return true
        }
      }
    }
  }

  return false
}

// Navigation guards to route users to correct path based on permission type.
//
// Access is closed by default: a route has to declare `isPublic` to be reachable without
// a login, and `groupsAllowed` narrows it further from there. The guard used to work the
// other way round, acting only when a route declared `groupsAllowed`, which meant any
// route added without that key was silently open to anyone with the URL.
router.beforeEach(async (to, from, next) => {
  const { isPublic, groupsAllowed } = to.meta
  const { isLoggedIn } = store.getters
  const userGroups = store.getters.userGroups || false
  const isAllowedToView = checkPermission(groupsAllowed, userGroups)

  // Check password expiration for logged-in users
  // if (isLoggedIn) {
  //   // Refresh user data to get latest password expiration status
  //   try {
  //     await store.dispatch('fetchUser')
  //   } catch (error) {
  //     console.error('Error fetching user data:', error)
  //   }

  //   //  check password expiration
  //   if (store.getters.user?.passwordExpired) {
  //     const toast = useToast()
  //     toast.error('Your password has expired. Please reset it to continue.')

  //     store.dispatch('setUser', null)
  //     next({
  //       name: 'RequestPasswordReset',
  //       query: { redirect: to.fullPath, reason: 'password_expired' },
  //     })
  //     return
  //   }
  // }

  if (to.name === 'Login' && isLoggedIn) {
    next({ name: 'Home' })
  } else if (isPublic) {
    next()
  } else if (!isLoggedIn) {
    next({ name: 'Login' })
  } else if (groupsAllowed && !isAllowedToView) {
    next({ name: 'PageNotFound' })
  } else {
    next()
  }
})

export default router
